import { computed, effect, inject, Injectable, signal, untracked, WritableSignal } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, NavigationExtras, Router, UrlSegment, UrlSegmentGroup } from '@angular/router';
import { map as radashMap } from 'radash';
import { firstValueFrom } from 'rxjs';
import { v4 } from 'uuid';

import { LayerBloc } from '@connect-services/client/blocs/layer.bloc';
import { LayerEntityBloc } from '@connect-services/client/blocs/layer-entity.bloc';
import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { FolderAndStandardByDepositId } from '@connect-services/client/components/project-settings/project-layers-settings/project-layers-settings.component';
import { bypassDeactivateModal } from '@connect-services/client/guards/can-dismount.guard';
import { NavigationStateService } from '@connect-services/client/guards/reload.guard';
import { RouteNameEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    Column,
    ColumnsService,
    DepositsService,
    DepositWithFolderAndStandard,
    LayerAttribute,
    LayerControllerGetEntities200ResponseAllOfItemsInner as DataEntityAdditionalProperties,
    LayerFilterResult,
    LayersService,
    LayerWithAttributes,
    ProjectsService,
} from '@connect-services/client/sdk/api';
import { AbstractFieldInterventionListService } from '@connect-services/client/services/abstract-field-intervention-list.service';
import { BroadcastChannelService } from '@connect-services/client/services/broadcast-channel.service';
import { DepositService } from '@connect-services/client/services/deposit.service';
import { EntityDetailCreationContextService } from '@connect-services/client/services/entity-detail-creation-context.service';
import { HomeService } from '@connect-services/client/services/home.service';
import {
    SelectedEntitiesByLayerId,
    SelectedEntity,
    SelectionStateService,
} from '@connect-services/client/services/map/selection/selection-state.service';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { TableService } from '@connect-services/client/services/table.service';
import { TableEditModeService } from '@connect-services/client/services/table-edit-mode.service';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import { SortState } from '@connect-services/src/lib/components/table/table.component';
import {
    defaultPaginationParameters,
    OrderEnum,
    paginationMaxTake,
    PaginationParameters,
} from '@shared/client/models/pagination.model';

export const defaultProjectEntitiesListPaginationParameters: Readonly<PaginationParameters> = {
    order: OrderEnum.ASC,
    orderBy: 'businessIdentifier',
    page: 0,
    take: 20,
};

export const COLUMN_NAMES_BATCH_SIZE = 50;

type ColumnsByName = Map<string, Column>;
type ActiveLayerFilterContext = {
    layerIds: string[];
    selectedEntityIds?: string[];
    shouldSkipFilterIds: boolean;
};

type OpenCreateEntityPanelOptions = {
    existingEntityId?: string;
    haveToFinalizeCreation?: boolean;
};

@Injectable({
    providedIn: 'root',
})
export class ProjectEntityTableService extends AbstractFieldInterventionListService<
    DataEntityAdditionalProperties,
    LayerWithAttributes
> {
    public readonly areLayersDefined = computed(() => !!(this.layerNames().length && this.layersByName()));
    public readonly columnsByName = signal<ColumnsByName | undefined>(undefined);
    public columnsToSearchValueIn: string[] = ['dataAttributes.businessIdentifier'];
    public readonly currentPanelContext = signal<{ depositId: string; entityId: string } | null>(null);
    public readonly currentPanelEntity: WritableSignal<string | null> = signal(null);
    public readonly deposits: WritableSignal<FolderAndStandardByDepositId> = signal(new Map());
    public readonly forceResetSearchBar = signal(false);
    public readonly forceTabIndex = signal(0);
    public readonly isOnlySelectedEntitiesTabs = signal<boolean>(false);
    public readonly layerNames: WritableSignal<string[]> = signal([]);
    public readonly layers: WritableSignal<LayerWithAttributes[]> = signal([]);
    public readonly layersByName: WritableSignal<Map<string, LayerWithAttributes[]> | null> = signal(null);
    public override readonly paginationParameters = signal<PaginationParameters>({
        ...defaultProjectEntitiesListPaginationParameters,
    });
    public readonly totalEntities = signal(0);
    public readonly unSelectedEntitiesAfterFilterByLayerId = signal<SelectedEntitiesByLayerId>({});

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly columnService = inject(ColumnsService);
    private readonly depositService = inject(DepositService);
    private readonly depositsService = inject(DepositsService);
    private readonly entityDetailCreationContextService = inject(EntityDetailCreationContextService);
    private readonly homeService = inject(HomeService);
    private readonly layerBloc = inject(LayerBloc);
    private readonly layerEntityBloc = inject(LayerEntityBloc);
    private readonly layerListBloc = inject(LayerListBloc);
    private readonly layersServiceSdk = inject(LayersService);
    private readonly mapFilterService = inject(MapFilterService);
    private readonly navigationService = inject(NavigationStateService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectServiceSdk = inject(ProjectsService);
    private readonly router = inject(Router);
    private readonly selectionStateService = inject(SelectionStateService);
    private readonly tableEditModeService = inject(TableEditModeService);
    private readonly tableService = inject(TableService);

    public constructor() {
        super();

        effect((): void => {
            if (!this.isOnlySelectedEntitiesTabs()) {
                untracked((): void => {
                    this.unSelectedEntitiesAfterFilterByLayerId.set({});
                });
            }
        });
    }

    public async changeTab(layerName: string): Promise<void> {
        this.isLoading.set(true);
        this.resetPagination();
        this.searchValue.set('');
        this.forceResetSearchBar.set(true);

        this.setActiveLayerByName(layerName);

        await this.fetchEntities();
        const columnsByName = await this.fetchColumns();
        this.columnsByName.set(columnsByName);

        this.isLoading.set(false);
    }

    public closeEntityDetailPanel(): void {
        const context = this.currentPanelContext();
        if (!context) {
            return;
        }

        this.currentPanelEntity.set(null);
        this.currentPanelContext.set(null);
    }

    public async ensureLayersLoaded(): Promise<void> {
        if (!this.layers().length) {
            await this.fetchProjectLayers();
        }
    }

    public override async fetchEntities(): Promise<void> {
        if (!this.activeLayers()?.length) {
            return;
        }

        const activeLayerFilterContext = this.getActiveLayerFilterContext();

        if (activeLayerFilterContext && !activeLayerFilterContext.layerIds.length) {
            this.entities.set([]);
            this.paginationParameters.update((parameters) => ({
                ...parameters,
                totalItems: 0,
            }));

            return;
        }

        const layerIds =
            activeLayerFilterContext?.layerIds ?? this.activeLayers().map((layer: LayerWithAttributes) => layer.id);

        const selectedEntitiesIds = this.getFetchFilterEntitiesIds(activeLayerFilterContext, layerIds);
        const hasSelectedEntitiesFilter = !!selectedEntitiesIds.length;

        const layerEntitiesResponse = await firstValueFrom(
            this.layersServiceSdk.layerControllerSearchEntitiesByLayers({
                filterLayerEntityDto: {
                    filterIds: activeLayerFilterContext?.shouldSkipFilterIds
                        ? undefined
                        : this.mapFilterService.selectedFilters(),
                    layerIds,
                    selectedEntities: hasSelectedEntitiesFilter ? selectedEntitiesIds : undefined,
                    selectedLayers: hasSelectedEntitiesFilter ? layerIds : undefined,
                },
                ...this.paginationParameters(),
                properties: this.searchValue().length ? this.columnsToSearchValueIn : undefined,
                value: this.searchValue(),
            }),
        );

        this.entities.set(layerEntitiesResponse.items);

        this.paginationParameters.update((parameters) => ({
            ...parameters,
            page: layerEntitiesResponse.page,
            take: layerEntitiesResponse.take,
            totalItems: layerEntitiesResponse.totalItems,
        }));
    }

    public async fetchProjectLayers(): Promise<void> {
        let hasNextPage = false;
        let page = 0;
        const allLayersWithAttributes: LayerWithAttributes[] = [];
        do {
            const layersPagination = await firstValueFrom(
                this.projectServiceSdk.projectControllerGetLayersWithAttributes({
                    id: this.projectBloc.value()!.id,
                    order: OrderEnum.DESC,
                    orderBy: 'name',
                    page: page,
                    take: paginationMaxTake,
                }),
            );
            allLayersWithAttributes.push(...layersPagination.items);
            hasNextPage = layersPagination.hasNextPage;
            page += 1;
        } while (hasNextPage);

        this.layersByName.set(
            allLayersWithAttributes.reduce(
                (acc: Map<string, LayerWithAttributes[]>, layerWithAttributes: LayerWithAttributes) => {
                    if (!acc.has(layerWithAttributes.name)) {
                        acc.set(layerWithAttributes.name, []);
                    }

                    acc.get(layerWithAttributes.name)!.push(layerWithAttributes);

                    return acc;
                },
                new Map(),
            ),
        );

        this.layers.set(allLayersWithAttributes);

        if (this.broadcastChannelService.currentLayer() && this.layersByName()?.entries()) {
            const layersByName = this.layersByName()?.entries();
            const layersMap = new Map<string, LayerWithAttributes[]>(layersByName);
            const layers = layersMap.get(this.broadcastChannelService.currentLayer()!);

            this.activeLayers.set(layers ?? []);

            return;
        }

        const uniqueLayerNames = Array.from(
            new Set(allLayersWithAttributes.map((layerWithAttributes) => layerWithAttributes.name)),
        ).sort();

        this.layerNames.set(uniqueLayerNames);

        if (this.router.url.includes('?')) {
            this.setForceTabIndex();
        }

        const layerName = this.isOnlySelectedEntitiesTabs()
            ? this.layerNames().find((name: string) => this.hasSelectedEntitiesForLayerName(name))
            : this.layerNames()[this.forceTabIndex()];

        const layers = layerName ? (this.layersByName()!.get(layerName) ?? []) : [];

        this.activeLayers.set(layers);
    }

    public getLayerByName(layerName: string): LayerWithAttributes | undefined {
        return this.layers()?.find((layer: LayerWithAttributes) => layer.name === layerName);
    }

    public async handleSearch(searchValue: string): Promise<void> {
        this.searchValue.set(searchValue);
        await this.handleSearchFilter();
    }

    public async handleSearchFilter(): Promise<void> {
        this.resetPagination();
        await this.fetchEntities();
    }

    public hasSelectedEntitiesForLayerName(layerName: string): boolean {
        const layers: LayerWithAttributes[] = this.layersByName()?.get(layerName) ?? [];

        const selectedByLayerId: SelectedEntitiesByLayerId = this.selectionStateService.selectedEntitiesByLayerId();
        const unSelectedByLayerId: SelectedEntitiesByLayerId = this.unSelectedEntitiesAfterFilterByLayerId();

        return layers.some(
            (layer: LayerWithAttributes) =>
                !!selectedByLayerId[layer.id]?.length || !!unSelectedByLayerId[layer.id]?.length,
        );
    }

    public async initializeTableWithData(): Promise<void> {
        this.isLoading.set(true);

        await this.fetchProjectLayers();
        await this.fetchEntities();
        await this.fetchDeposits();

        const columnsByName = await this.fetchColumns();
        this.columnsByName.set(columnsByName);

        this.isLoading.set(false);
    }

    public async onPaginationChange(page: PageEvent): Promise<void> {
        this.paginationParameters.update((parameters: PaginationParameters) => ({
            ...parameters,
            page: page.pageIndex,
            take: page.pageSize,
        }));

        await this.fetchEntities();
    }

    public onPaginatorChange(paginator: PaginatorComponent): void {
        this.paginator = paginator;
    }

    public async onSortChange(sort: SortState | undefined): Promise<void> {
        if (!sort) {
            this.paginationParameters.update((parameters) => ({
                ...parameters,
                order: defaultProjectEntitiesListPaginationParameters.order,
                orderBy: defaultProjectEntitiesListPaginationParameters.orderBy,
            }));

            await this.fetchEntities();

            return;
        }

        const parameters = this.tableService.onSortStateChange(sort!, this.paginationParameters());

        this.paginationParameters.set({
            ...parameters,
        });

        await this.fetchEntities();
    }

    public async openCreateEntityPanel(
        layerId: string,
        layerName: string,
        options: OpenCreateEntityPanelOptions = {},
    ): Promise<void> {
        const { existingEntityId, haveToFinalizeCreation } = options;

        await this.ensureLayersLoaded();

        if (haveToFinalizeCreation) {
            this.entityDetailCreationContextService.finalizeCurrentCreationNow();
        }

        const layerIndex = this.selectActiveLayer(layerName);
        if (layerIndex < 0) {
            return;
        }

        const layersForName: LayerWithAttributes[] = this.layersByName()?.get(layerName) ?? [];
        if (!layersForName.length) {
            return;
        }

        const deposits: DepositWithFolderAndStandard[] = await this.fetchDepositsForLayers(layersForName);
        if (!deposits.length) {
            return;
        }

        const depositIds: string[] = deposits.map((deposit: DepositWithFolderAndStandard) => deposit.id);
        const layerIds: string[] = layersForName.map((layer: LayerWithAttributes) => layer.id);

        this.navigationService.markNavigated();

        const entityId = existingEntityId ?? v4();

        if (!existingEntityId) {
            this.entityDetailCreationContextService.initFromTabular({
                depositIds,
                entityId,
                layerIds,
            });
        }

        this.homeService.isExternalPage()
            ? await this.openCreateEntityPanelExternal(depositIds, entityId, layerId, haveToFinalizeCreation)
            : await this.openCreateEntityPanelInternal(layerId, entityId, haveToFinalizeCreation);
    }

    public resetData(): void {
        this.activeLayers.set([]);
        this.currentSources.set([]);
        this.searchValue.set('');
        this.entities.set([]);
        this.forceTabIndex.set(0);
    }

    public override resetPagination(): void {
        if (!this.paginator) {
            return;
        }

        this.paginationParameters.set(defaultProjectEntitiesListPaginationParameters);
        this.paginator.resetPaginator();
    }

    public selectActiveLayer(layerName: string): number {
        this.setActiveLayerByName(layerName);

        const layerIndex = this.layerNames().findIndex((name) => name === layerName);
        if (layerIndex >= 0) {
            this.forceTabIndex.set(layerIndex);
        }

        return layerIndex;
    }

    public setActiveLayerByName(activeLayerName: string): void {
        const layersByName = this.layersByName();
        if (!layersByName) {
            return;
        }

        const layer = layersByName!.get(activeLayerName);
        if (!layer?.length) {
            return;
        }

        this.activeLayers.set(layer);

        this.paginationParameters.set({
            ...this.paginationParameters(),
            page: defaultPaginationParameters.page,
        });
    }

    public setForceTabIndex(): void {
        if (!this.currentPanelEntity()) {
            this.forceTabIndex.set(0);

            return;
        }

        const index = this.layerNames().indexOf(this.layerBloc.value()!.name);
        this.forceTabIndex.set(index);
    }

    public setUrlQueryParams(extras?: NavigationExtras): NavigationExtras {
        const finalExtras: NavigationExtras = extras ?? {};

        for (const paramName of ['ext', 'layerName']) {
            const queryParam = this.activatedRoute.snapshot.queryParamMap.get(paramName);
            if (!queryParam) {
                continue;
            }

            finalExtras.queryParams = {
                ...(finalExtras.queryParams ?? {}),
                [paramName]: decodeURI(queryParam),
            };
        }

        return finalExtras;
    }

    public async toggleCreateEntityDetailPanelExternal(
        depositIds: string[],
        entityId: string,
        layerId: string,
        haveToFinalizeCreation?: boolean,
    ): Promise<void> {
        this.layerBloc.reset();
        this.layerEntityBloc.reset();
        this.layerListBloc.reset();

        this.currentPanelEntity.set(entityId);
        this.currentPanelContext.set({ depositId: depositIds[0], entityId });

        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return;
        }

        const extras: NavigationExtras = {
            queryParams: {
                deposit: depositIds,
                layer: layerId,
            },
            queryParamsHandling: 'merge',
            ...(haveToFinalizeCreation === false ? { info: bypassDeactivateModal } : {}),
        };

        this.setUrlQueryParams(extras);

        await this.router.navigate(
            [
                RoutePathEnum.PROJECTS,
                projectId,
                RoutePathEnum.LAYERS,
                layerId,
                RouteNameEnum.ENTITY_DETAIL,
                entityId,
                'CREATE',
            ],
            extras,
        );
    }

    public async toggleEntityDetailPanel(depositId: string, entityId: string): Promise<void> {
        this.layerBloc.reset();
        this.layerEntityBloc.reset();
        this.layerListBloc.reset();

        const segments: string[] = this.getUrlSegments(entityId);
        const isSameEntity = entityId === this.currentPanelEntity();

        this.currentPanelEntity.set(isSameEntity ? null : entityId);
        this.currentPanelContext.set(isSameEntity ? null : { depositId, entityId });

        const matchingLayer =
            this.activeLayers().find((layer: LayerWithAttributes) => layer.deposit === depositId) ??
            this.activeLayers()[0];

        const baseParams: Record<string, string> = { deposit: depositId, layer: matchingLayer.id };

        const extras: NavigationExtras = isSameEntity
            ? {}
            : {
                  queryParams: baseParams,
                  queryParamsHandling: 'merge',
              };

        this.setUrlQueryParams(extras);

        await this.router.navigate(segments, extras);
    }

    protected override buildEntityFromMapFeature(): DataEntityAdditionalProperties | undefined {
        return undefined;
    }

    protected fetchAllEntities(): Promise<[]> {
        return Promise.resolve([]);
    }

    protected getEntityId(entity: DataEntityAdditionalProperties): string {
        return entity['uuid'];
    }

    protected getLayerId(entity: DataEntityAdditionalProperties): string {
        return entity['layer_id'];
    }

    private buildFullyFilteredLayerContext(
        activeLayers: LayerWithAttributes[],
        fullyFilteredLayerIds: Set<string>,
    ): ActiveLayerFilterContext {
        return {
            layerIds: activeLayers
                .map((layer: LayerWithAttributes) => layer.id)
                .filter((layerId: string) => !fullyFilteredLayerIds.has(layerId)),
            shouldSkipFilterIds: true,
        };
    }

    private buildSelectedEntitiesFilterContext(
        activeLayers: LayerWithAttributes[],
        activeLayerResults: LayerFilterResult[],
    ): ActiveLayerFilterContext {
        return {
            layerIds: activeLayers.map((layer: LayerWithAttributes) => layer.id),
            selectedEntityIds: [...new Set(activeLayerResults.flatMap((result) => result.entityIds))],
            shouldSkipFilterIds: true,
        };
    }

    private async fetchColumns(): Promise<ColumnsByName> {
        const layer = this.activeLayers()[0];
        const columnsByName = new Map<string, Column>();

        if (!layer?.deposit || !layer.tableName) {
            return columnsByName;
        }

        const deposit = this.deposits().get(layer.deposit);
        const columnNames = layer.dataAttributes
            .filter((attribute: LayerAttribute) => attribute.isBookmarked)
            .map((attribute: LayerAttribute) => attribute.column);
        if (!deposit?.standard || !columnNames.length) {
            return columnsByName;
        }

        const standardId = deposit.standard.id;
        const tableName = layer.tableName;

        const columnNamesBatches: string[][] = [];
        for (let index = 0; index < columnNames.length; index += COLUMN_NAMES_BATCH_SIZE) {
            columnNamesBatches.push(columnNames.slice(index, index + COLUMN_NAMES_BATCH_SIZE));
        }

        const columnsByBatch = await Promise.all(
            columnNamesBatches.map((columnNamesBatch: string[]) =>
                firstValueFrom(
                    this.columnService.columnControllerGetByName({
                        columnNames: columnNamesBatch,
                        standard: standardId,
                        tableName: tableName,
                    }),
                ),
            ),
        );
        const columns = columnsByBatch.flat();

        if (!columns.length) {
            return columnsByName;
        }

        columns.forEach((column: Column) => columnsByName.set(column.name, column));

        return columnsByName;
    }

    private async fetchDeposits(): Promise<void> {
        const deposits: FolderAndStandardByDepositId =
            await this.depositService.getDepositsWithFolderAndStandardForLayers(this.layers());

        this.deposits.set(deposits);
    }

    private fetchDepositsForLayers(layers: LayerWithAttributes[]): Promise<DepositWithFolderAndStandard[]> {
        const depositIds = new Set(
            layers
                .map((layer: LayerWithAttributes) => layer.deposit)
                .filter((depositId): depositId is string => depositId !== null),
        );

        return radashMap([...depositIds], (depositId) =>
            firstValueFrom(
                this.depositsService.depositControllerGetDepositWithFolderAndStandard({
                    id: depositId,
                }),
            ),
        );
    }

    private getActiveLayerFilterContext(): ActiveLayerFilterContext | null {
        if (!this.hasResolvedActiveFilters()) {
            return null;
        }

        const activeLayers = this.activeLayers();
        const activeLayerResults = this.getCurrentActiveLayerResults(activeLayers);

        if (!activeLayerResults.length) {
            return null;
        }

        const isPreview = !!this.mapFilterService.previewResultFilters();
        const fullyFilteredLayerIds = this.getFullyFilteredLayerIds(activeLayerResults);

        if (!isPreview) {
            return {
                layerIds: activeLayers
                    .map((layer: LayerWithAttributes) => layer.id)
                    .filter((layerId: string) => !fullyFilteredLayerIds.has(layerId)),
                shouldSkipFilterIds: false,
            };
        }

        // Preview filters do not have persisted filterIds.
        // Keep the existing selectedEntities behavior for now.
        // Large previews may require a dedicated backend pagination strategy.
        if (fullyFilteredLayerIds.size) {
            return this.buildFullyFilteredLayerContext(activeLayers, fullyFilteredLayerIds);
        }

        if (!this.hasEntitySelectionForAllActiveLayers(activeLayers, activeLayerResults)) {
            return null;
        }

        return this.buildSelectedEntitiesFilterContext(activeLayers, activeLayerResults);
    }

    private getCurrentActiveLayerResults(activeLayers: LayerWithAttributes[]): LayerFilterResult[] {
        const activeLayerIds = new Set(activeLayers.map((layer: LayerWithAttributes) => layer.id));

        return (this.mapFilterService.previewResultFilters() ?? this.mapFilterService.appliedFiltersResult()).filter(
            ({ layerId }: { layerId: string }) => activeLayerIds.has(layerId),
        );
    }

    private getFetchFilterEntitiesIds(
        activeLayerFilterContext: ActiveLayerFilterContext | null,
        layerIds: string[],
    ): string[] {
        const selectedEntitiesIds = [...(activeLayerFilterContext?.selectedEntityIds ?? [])];

        if (!this.isOnlySelectedEntitiesTabs()) {
            return selectedEntitiesIds;
        }

        const selectedByLayerId: Record<string, SelectedEntity[]> =
            this.selectionStateService.selectedEntitiesByLayerId();

        const unSelectedByLayerId: Record<string, SelectedEntity[]> = this.unSelectedEntitiesAfterFilterByLayerId();

        layerIds.forEach((layerId: string) => {
            const entities: SelectedEntity[] = [
                ...(selectedByLayerId[layerId] ?? []),
                ...(unSelectedByLayerId[layerId] ?? []),
            ];

            selectedEntitiesIds.push(
                ...entities
                    .filter((entity: SelectedEntity) => !entity.isInCreationState)
                    .map((entity: SelectedEntity): string => entity.uuid),
            );
        });

        return [...new Set(selectedEntitiesIds)];
    }

    private getFullyFilteredLayerIds(activeLayerResults: LayerFilterResult[]): Set<string> {
        return new Set(
            activeLayerResults
                .filter((result: LayerFilterResult) => result.filtered && !result.entityIds.length)
                .map((result: LayerFilterResult) => result.layerId),
        );
    }

    private getUrlSegments(entityId: string): string[] {
        const isExternalWindow = this.router.url.indexOf(`/${RoutePathEnum.EXT}`) > -1;
        const tree = this.router.parseUrl(this.router.url);
        const primary: UrlSegmentGroup = tree.root.children['primary'];
        const segments: UrlSegment[] = primary ? primary.segments : [];

        const path: string[] = segments.map((segment: UrlSegment) => segment.path);

        if (entityId === this.currentPanelEntity()) {
            return path.slice(0, 2);
        }

        const finalPath = isExternalWindow ? path : [RoutePathEnum.PROJECTS, this.projectBloc.value()!.id];
        finalPath.push(...[RoutePathEnum.TABLE_ENTITY_DETAIL, entityId]);

        return finalPath;
    }

    private hasEntitySelectionForAllActiveLayers(
        activeLayers: LayerWithAttributes[],
        activeLayerResults: LayerFilterResult[],
    ): boolean {
        const activeLayerResultsByLayerId = new Map<string, LayerFilterResult>(
            activeLayerResults.map((result: LayerFilterResult) => [result.layerId, result]),
        );

        return activeLayers.every((layer: LayerWithAttributes) => {
            const result = activeLayerResultsByLayerId.get(layer.id);

            return !!result && !!result.entityIds.length;
        });
    }

    private hasResolvedActiveFilters(): boolean {
        const hasActiveFilters =
            !!this.mapFilterService.selectedFilters().length || this.mapFilterService.previewResultFilters() !== null;

        return hasActiveFilters && this.mapFilterService.hasResolvedAppliedFilters();
    }

    private async openCreateEntityPanelExternal(
        depositIds: string[],
        entityId: string,
        layerId: string,
        haveToFinalizeCreation?: boolean,
    ): Promise<void> {
        if (!this.tableEditModeService.isExternalTableEditActive()) {
            return;
        }

        this.tableEditModeService.enterEntityDetailEditMode(entityId);

        await this.toggleCreateEntityDetailPanelExternal(depositIds, entityId, layerId, haveToFinalizeCreation);
    }

    private async openCreateEntityPanelInternal(
        layerId: string,
        entityId: string,
        haveToFinalizeCreation: boolean = false,
    ): Promise<void> {
        const projectId = this.projectBloc.value()?.id;

        if (!projectId) {
            return;
        }

        const extras: NavigationExtras = !haveToFinalizeCreation ? { info: bypassDeactivateModal } : {};

        await this.router.navigate(
            [
                RoutePathEnum.PROJECTS,
                projectId,
                RoutePathEnum.LAYERS,
                layerId,
                RouteNameEnum.ENTITY_DETAIL,
                entityId,
                'CREATE',
            ],
            extras,
        );
    }
}
